import { Form, Formik, useFormikContext } from "formik"
import { Grid, Typography } from "@mui/material"
import { useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"
import * as yup from "yup"
import {
    CompanyAdminUserGroupsQuery,
    DomainDetailsResponse,
    createDomain,
    fetchDomainDetails,
    updateDomain,
} from "../userGroupsApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ConfirmedDomain, CreateUpdateDomainFormValues } from "../userGroupsTypes"
import { FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"

const useStyles = makeStyles()((theme) => ({
    descriptionText: {
        color: theme.palette.tailwind.gray[500],
    },

    dialogButton: {
        margin: "2rem auto 0",
    },
}))
type CreateUpdateDomainFormProps = Readonly<{
    domainId: number | null
    onSaveDomain: (data?: ConfirmedDomain) => void
}>

const selectDomainDetails = (data: DomainDetailsResponse) => {
    const domainData = data.domain
    if (!domainData) return null
    const { domain, username } = domainData

    return { domain, username }
}

export const CreateUpdateDomainForm = ({ domainId, onSaveDomain }: CreateUpdateDomainFormProps) => {
    const { l10n } = useAppLocalization()
    const { userGroupId } = useParams()

    const domainDetailsResult = useApiQuery(fetchDomainDetails, {
        queryName: CompanyAdminUserGroupsQuery.DOMAIN_DETAILS,
        dependencies: {
            domainId: domainId || 0,
        },
        enabled: Boolean(domainId),
        select: selectDomainDetails,
    })

    const { mutate: callCreateDomain } = useApiMutation(createDomain, {
        async onSuccess(_, data) {
            const { id, domain, isConfirmed } = data.domain
            onSaveDomain({ id, domain, isConfirmed })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
        },
    })
    const { mutate: callUpdateDomain } = useApiMutation(updateDomain, {
        async onSuccess(params, data, context) {
            onSaveDomain()
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(CompanyAdminUserGroupsQuery.DOMAIN_DETAILS, { domainId: params.domainId }),
            })
            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse.displayMessage,
            })
        },
    })

    const defaultInitialValues = {
        domain: "",
        username: "",
    }

    const validationSchema = yup.object({
        domain: yup.string().required(l10n.getString("required-field")),
        username: yup.string().required(l10n.getString("required-field")),
    })

    function handleSubmit(values: CreateUpdateDomainFormValues) {
        if (domainId) {
            return callUpdateDomain({ domain: { username: values.username }, domainId })
        } else {
            return callCreateDomain({ domain: values, userGroupId: userGroupId || "" })
        }
    }

    return (
        <>
            <Formik<CreateUpdateDomainFormValues>
                enableReinitialize
                initialValues={domainDetailsResult.data || defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <CreateUpdateDomainFormContent isDisabledDomain={!!domainId} />
                </Form>
            </Formik>
        </>
    )
}

type CreateUpdateDomainFormContentProps = Readonly<{
    isDisabledDomain: boolean
}>

export function CreateUpdateDomainFormContent({ isDisabledDomain }: CreateUpdateDomainFormContentProps) {
    const { values } = useFormikContext<CreateUpdateDomainFormValues>()
    const { classes } = useStyles()

    return (
        <>
            <Grid container flexDirection="column" gap={2.5}>
                <LocalizedStrict
                    id="create-or-edit-company-user-group-connected-domains-modal-field-domain"
                    attrs={{ label: true, placeholder: true }}
                >
                    <FormikTextField
                        name="domain"
                        label="Domain"
                        placeholder="Enter domain"
                        fullWidth
                        disabled={isDisabledDomain}
                    />
                </LocalizedStrict>
                <Grid container flexWrap="nowrap" alignItems="center" gap={1.25}>
                    <LocalizedStrict
                        id="create-or-edit-company-user-group-connected-domains-modal-field-username"
                        attrs={{ label: true, placeholder: true }}
                    >
                        <FormikTextField name="username" label="User name" placeholder="Enter user name" />
                    </LocalizedStrict>
                    {values.domain && (
                        <Typography variant="body2" className={classes.descriptionText}>
                            @{values.domain}
                        </Typography>
                    )}
                </Grid>
            </Grid>
            <LocalizedStrict id="create-or-edit-company-user-group-connected-domains-modal-send-invitation">
                <StandardButton type="submit" className={classes.dialogButton}>
                    Send invitation
                </StandardButton>
            </LocalizedStrict>
        </>
    )
}
